import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Breadcrumbs, Pagination, Typography } from '@mui/material';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import { ProjectContext } from '../../../../providers/projectProvider';
import { useToaster } from '../../../../providers/ToasterProvider';
import { downloadVenueDeviceCsv, getVenueDevices, getVenueDeviceFilters } from '../../../../services/venueAuditService';
import { VenueAuditTable } from './VenueAuditTable';
import { VenueDeviceDetailsModal } from './VenueDeviceDetailsModal';
import { ALL_VENUES, EMPTY_AUDIT_OPTIONS, HardwareAuditFilters } from './HardwareAuditFilters';
import { saveBlobAsFile } from './saveBlobAsFile';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import ACTION_VIEW from '../../../../assets/images/action-view.svg';

// Each matching device: where it is, every dropdown's value with the hardware behind it (selected
// dropdowns first), then the general hardware details. Dropdown and detail columns come from the
// filter options.
const LEADING_COLUMNS = [
    { id: 'srNo', label: 'Sr No', minWidth: 70 },
    { id: 'venueName', label: 'Venue', minWidth: 160 },
    { id: 'labNo', label: 'Lab', minWidth: 90 },
    { id: 'seatNo', label: 'Seat', minWidth: 90 },
    { id: 'macAddress', label: 'MAC Address', minWidth: 150 },
];
const ACTION_COLUMN = { id: 'actions', label: 'Action', minWidth: 80 };

const PAGE_LIMIT = 10;
const EMPTY_VALUE = '-';

const isEmpty = (value) => value === null || value === undefined || value === '';

const withoutKey = (object, key) => Object.fromEntries(Object.entries(object).filter(([entryKey]) => entryKey !== key));

// A dropdown's value, with the hardware behind it underneath.
const FacetCell = ({ value, details }) => (
    <Box sx={{ textAlign: 'left' }}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>{value ?? EMPTY_VALUE}</Typography>
        {!isEmpty(details) && <Typography variant='caption' sx={{ color: 'grey.600', display: 'block', lineHeight: 1.3 }}>{details}</Typography>}
    </Box>
);

export const VenueDeviceList = () => {

    const navigate = useNavigate();
    // The sidebar opens the dashboard without a venue: every venue.
    const { projectName, venueId = ALL_VENUES } = useParams();
    const { projectDetails } = useContext(ProjectContext);
    const { addNotification } = useToaster();
    const [venue, setVenue] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [maxPages, setMaxPages] = useState(0);
    const [matchedCount, setMatchedCount] = useState(0);
    const [scopeCount, setScopeCount] = useState(0);
    const [counts, setCounts] = useState(null);
    const [filterOptions, setFilterOptions] = useState(EMPTY_AUDIT_OPTIONS);
    const [examFilters, setExamFilters] = useState({});
    const [facetFilters, setFacetFilters] = useState({});
    const [isDownloading, setIsDownloading] = useState(false);

    // Selected dropdowns come first, so what was filtered on sits next to the seat.
    const orderedFacets = [
        ...filterOptions.facets.filter((facet) => facetFilters[facet.key] !== undefined),
        ...filterOptions.facets.filter((facet) => facetFilters[facet.key] === undefined),
    ];
    const columns = [
        ...LEADING_COLUMNS,
        ...orderedFacets.map((facet) => ({ id: `facet.${facet.key}`, label: facet.name, minWidth: 180, align: 'left' })),
        ...(filterOptions.summary ?? []).map((part) => ({ id: `summary.${part.key}`, label: part.name, minWidth: 200, align: 'left' })),
        ACTION_COLUMN,
    ];

    // Exam filters belong to one venue's exams, so they start over when the venue changes; the
    // hardware dropdowns are kept.
    useEffect(() => {
        setCurrentPage(1);
        setExamFilters({});

        const fetchFilterOptions = async () => {
            try {
                const res = await getVenueDeviceFilters(venueId);
                if (res?.code === 'OK') {
                    setFilterOptions({ ...EMPTY_AUDIT_OPTIONS, ...res.data });
                }
            } catch (error) {
                console.log('There is an error while fetching venue device filters', error);
            }
        }
        fetchFilterOptions();
    }, [venueId]);

    useEffect(() => {
        const fetchVenueDevices = async () => {
            try {
                const res = await getVenueDevices(venueId, {
                    page: currentPage,
                    limit: PAGE_LIMIT,
                    examFilters,
                    facetFilters,
                });
                if (res?.code === 'OK') {
                    const count = res.data?.count ?? 0;
                    setVenue(res.data?.venue ?? null);
                    setDevices(Array.isArray(res.data?.devices) ? res.data.devices : []);
                    setMatchedCount(count);
                    setScopeCount(res.data?.scopeCount ?? 0);
                    setCounts(res.data?.counts ?? null);
                    setMaxPages(Math.ceil(count / PAGE_LIMIT));
                }
            } catch (error) {
                console.log('There is an error while fetching venue devices', error);
            }
        }
        fetchVenueDevices();
    }, [venueId, currentPage, examFilters, facetFilters]);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    }

    const handleVenueChange = (value) => {
        navigate(value === ALL_VENUES ? `/${projectName}/venue-audit-dashboard` : `/${projectName}/venue-audit-dashboard/${value}`);
    }

    // A filter applies as soon as it changes, so every dropdown's counts stay current.
    const handleExamChange = (filterId, value) => {
        setCurrentPage(1);
        setExamFilters((filters) => (value === undefined ? withoutKey(filters, filterId) : { ...filters, [filterId]: value }));
    }

    const handleFacetChange = (facetKey, value) => {
        setCurrentPage(1);
        setFacetFilters((filters) => (value === undefined ? withoutKey(filters, facetKey) : { ...filters, [facetKey]: value }));
    }

    const handleReset = () => {
        setCurrentPage(1);
        setExamFilters({});
        setFacetFilters({});
    }

    const handleDownloadCsv = async () => {
        setIsDownloading(true);
        try {
            const response = await downloadVenueDeviceCsv(venueId, { examFilters, facetFilters });
            const name = venueId === ALL_VENUES ? 'all-venues' : (venue?.venueName ?? `venue-${venueId}`);
            saveBlobAsFile(response, `${name}-hardware-audit.csv`);
            addNotification('CSV downloaded successfully', 'success');
        } catch (error) {
            console.log('Error downloading venue hardware audit csv', error);
            addNotification('Failed to download CSV', 'danger');
        } finally {
            setIsDownloading(false);
        }
    }


    const renderTableCell = (columnId, row, rowIndex) => {
        if (columnId.startsWith('facet.')) {
            const key = columnId.slice('facet.'.length);
            return <FacetCell value={row.facets?.[key]} details={row.details?.facets?.[key]} />;
        }
        if (columnId.startsWith('summary.')) {
            const text = row.details?.hardware?.[columnId.slice('summary.'.length)];
            return isEmpty(text) ? EMPTY_VALUE : <Typography variant='body2' sx={{ textAlign: 'left' }}>{text}</Typography>;
        }
        switch (columnId) {
            case 'srNo':
                return rowIndex + 1 + ((currentPage - 1) * PAGE_LIMIT);
            case 'actions':
                return <Box component='span' sx={{ cursor: 'pointer' }} onClick={() => setSelectedDevice(row)}>
                    <img src={ACTION_VIEW} alt='' />
                </Box>;
            default:
                return row?.[columnId] ?? EMPTY_VALUE;
        }
    }

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>
                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Venue Audit Dashboard</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>
                <h1 className='title'>Venue Audit Dashboard{venue && venueId !== ALL_VENUES ? ` · ${venue.venueName}` : ''}</h1>

                <HardwareAuditFilters
                    venueId={venueId}
                    options={filterOptions}
                    examFilters={examFilters}
                    facetFilters={facetFilters}
                    counts={counts}
                    matchedCount={matchedCount}
                    scopeCount={scopeCount}
                    onVenueChange={handleVenueChange}
                    onExamChange={handleExamChange}
                    onFacetChange={handleFacetChange}
                    onReset={handleReset}
                    onDownload={handleDownloadCsv}
                    isDownloading={isDownloading}
                />

                <VenueAuditTable
                    columns={columns}
                    rows={devices}
                    renderCell={renderTableCell}
                    getRowKey={(row) => row.id}
                />

                <Pagination sx={{ mt: 'auto' }} count={Math.max(maxPages, 1)} page={currentPage} onChange={handlePageChange} shape="rounded" />

                <VenueDeviceDetailsModal
                    device={selectedDevice}
                    options={filterOptions}
                    onClose={() => setSelectedDevice(null)}
                />
            </Container>
        </MainContainer>
    );
}
